import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CityDetail,
  type CityListItem,
  type CreateCityRequest,
  CreateCityRequestSchema,
  type UpdateCityRequest,
  UpdateCityRequestSchema,
} from '@utazzo/shared';
import {
  UzButton,
  UzCollapsibleSection,
  UzCombobox,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzMultiSelect,
  UzSelect,
  UzSwitch,
} from '@utazzo/ui';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useFormContext, useWatch } from 'react-hook-form';

import { useTboCityOptions } from '../../tbo/use-tbo';
import { useCountries, useCreateCity, useStates, useUpdateCity } from '../use-cities';

import { CityTransportSection } from './CityTransportSection';

interface CityFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  city?: CityDetail | CityListItem | null;
}

interface CountryOptionItem {
  value: string;
  label: string;
  /** TBO country code (ISO-2, upper) used to scope the TBO city dropdown. */
  tboCode: string;
}

const CREATE_DEFAULTS: CreateCityRequest = {
  countryId: '',
  name: '',
  iataCode: '',
  customCityCode: '',
  neighbouringAirportCodes: [],
  tboCityId: null,
  additionalTboCityIds: [],
  isActive: true,
  isWebsite: false,
};

/**
 * Comma-separated IATA codes -> the array the API stores. Typed text stays as
 * typed (a trailing comma is not eaten mid-entry); the committed value is the
 * cleaned, uppercased, de-duplicated list.
 */
const parseAirportCodes = (text: string): string[] =>
  Array.from(
    new Set(
      text
        .toUpperCase()
        .split(/[,\s]+/)
        .map((c) => c.trim())
        .filter(Boolean),
    ),
  );

const AirportCodesInput = ({
  value,
  onChange,
  error,
  disabled,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  error?: string;
  disabled?: boolean;
}): React.JSX.Element => {
  const [text, setText] = useState(value.join(', '));
  // Re-sync when the form is reset to another city.
  const joined = value.join(', ');
  useEffect(() => {
    setText((current) => (parseAirportCodes(current).join(', ') === joined ? current : joined));
  }, [joined]);
  return (
    <UzInput
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onChange(parseAirportCodes(e.target.value));
      }}
      label="Neighbouring airport codes"
      placeholder="e.g. ZRH, BRN, GVA"
      hint="Optional. Other airports a traveller to this city can fly via, 3-letter IATA codes separated by commas. Offered beside the city's own code when a flight is searched for an itinerary or added to a fixed departure."
      error={error}
      disabled={disabled}
    />
  );
};

/**
 * The field set is identical for create + edit; it reads the form via context
 * (UzFormField → useFormContext), so it renders correctly inside whichever
 * UzForm wraps it. Keeping it as a subcomponent avoids duplicating fields
 * across the two resolver-typed forms.
 *
 * The State dropdown is server-driven: it lists states for the currently
 * selected country (GET /states), and is cleared when the country changes so a
 * stale state can't be submitted under the wrong country.
 *
 * The "TBO City" dropdown (stored as `tboCityId`) is likewise server-driven:
 * it lists TBO cities for the selected country's ISO code (GET /tbo/cities/
 * options) so an operator maps the City Master row to a TBO inventory city.
 */
const CityFields = ({
  pending,
  countryOptions,
  countriesLoading,
}: {
  pending: boolean;
  countryOptions: ReadonlyArray<CountryOptionItem>;
  countriesLoading: boolean;
}): React.JSX.Element => {
  const { setValue } = useFormContext();
  const countryId = useWatch({ name: 'countryId' }) as string | undefined;
  const statesQuery = useStates(countryId ?? null);

  const stateOptions = useMemo(
    () => (statesQuery.data ?? []).map((s) => ({ value: s.id, label: s.name })),
    [statesQuery.data],
  );

  const statePlaceholder = !countryId
    ? 'Select a country first'
    : statesQuery.isLoading
      ? 'Loading states…'
      : stateOptions.length === 0
        ? 'No states for this country (optional)'
        : 'Select a state (optional)';

  // TBO city combobox - scoped to the selected country's TBO (ISO-2) code, with
  // server-side search (the endpoint caps results, so typing is how an operator
  // reaches a city beyond the first page).
  const tboCode = useMemo(
    () => countryOptions.find((o) => o.value === countryId)?.tboCode ?? null,
    [countryOptions, countryId],
  );
  const [tboCitySearch, setTboCitySearch] = useState('');
  const tboCitiesQuery = useTboCityOptions(tboCode, tboCitySearch);
  const tboCityId = useWatch({ name: 'tboCityId' }) as string | null | undefined;

  const tboCityOptions = useMemo(() => {
    const opts = (tboCitiesQuery.data ?? []).map((c) => ({
      value: c.cityId,
      label: `${c.name} (${c.cityId})`,
    }));
    // Keep the persisted value selectable even if it falls outside the capped /
    // search-narrowed option list when editing an existing city.
    if (tboCityId && !opts.some((o) => o.value === tboCityId)) {
      return [{ value: tboCityId, label: tboCityId }, ...opts];
    }
    return opts;
  }, [tboCitiesQuery.data, tboCityId]);

  // Options for the "Additional TBO cities" multi-select: the same server-searched
  // list (use the TBO City search above to load a region), minus the primary, plus
  // any already-saved extra ids so they stay visible when editing.
  const additionalTboCityIds =
    (useWatch({ name: 'additionalTboCityIds' }) as string[] | undefined) ?? [];
  const additionalTboCityOptions = useMemo(() => {
    const base = (tboCitiesQuery.data ?? [])
      .filter((c) => c.cityId !== tboCityId)
      .map((c) => ({ value: c.cityId, label: `${c.name} (${c.cityId})` }));
    const missing = additionalTboCityIds
      .filter((id) => !base.some((o) => o.value === id))
      .map((id) => ({ value: id, label: id }));
    return [...missing, ...base];
  }, [tboCitiesQuery.data, tboCityId, additionalTboCityIds]);

  const tboCityPlaceholder = !countryId ? 'Select a country first' : 'Select a TBO city';

  return (
    <div className="space-y-3">
      <UzCollapsibleSection title="Location" description="Country, state, and the TBO mapping">
        <UzFormField name="countryId">
          {({ field, error }) => (
            <UzSelect
              name={field.name}
              label="Country"
              required
              placeholder={countriesLoading ? 'Loading countries…' : 'Select a country'}
              options={countryOptions}
              value={(field.value as string) || undefined}
              onValueChange={(v) => {
                field.onChange(v);
                // Clear the state when the country changes (user-driven only;
                // reset() during edit prefill does not trigger this).
                setValue('stateId', undefined, { shouldValidate: false });
              }}
              error={error}
              disabled={pending || countriesLoading}
            />
          )}
        </UzFormField>
        <UzFormField name="stateId">
          {({ field, error }) => (
            <UzSelect
              name={field.name}
              label="State"
              placeholder={statePlaceholder}
              options={stateOptions}
              value={(field.value as string) || undefined}
              onValueChange={field.onChange}
              error={error}
              disabled={pending || !countryId || statesQuery.isLoading}
            />
          )}
        </UzFormField>
        <UzFormField name="tboCityId">
          {({ field, error }) => (
            <UzCombobox
              name={field.name}
              label="TBO City"
              hint="Maps this city to a TBO inventory city. Search by name. Use the × to remove a link."
              placeholder={tboCityPlaceholder}
              searchPlaceholder="Search TBO cities…"
              options={tboCityOptions}
              value={(field.value as string | null | undefined) || undefined}
              onValueChange={field.onChange}
              onClear={() => field.onChange(null)}
              clearable
              onSearchChange={setTboCitySearch}
              loading={tboCitiesQuery.isFetching}
              emptyText={
                tboCode ? 'No TBO cities match (run a TBO sync if empty)' : 'Select a country first'
              }
              error={error}
              disabled={pending || !countryId}
            />
          )}
        </UzFormField>
        <UzFormField name="additionalTboCityIds">
          {({ field, error }) => (
            <UzMultiSelect
              name={field.name}
              label="Additional TBO cities"
              hint="Nearby TBO regions searched ALONGSIDE the primary and merged (e.g. Ubud + Payangan). Search a region in the TBO City field above to load options here."
              placeholder={tboCityPlaceholder}
              searchPlaceholder="Filter loaded TBO cities…"
              options={additionalTboCityOptions}
              values={(field.value as string[] | undefined) ?? []}
              onValuesChange={field.onChange}
              error={error}
              disabled={pending || !countryId}
              emptyText={
                tboCode
                  ? 'Search a region in the TBO City field above to load options'
                  : 'Select a country first'
              }
            />
          )}
        </UzFormField>
      </UzCollapsibleSection>
      <UzCollapsibleSection title="Identity & codes" description="Name and optional 3-letter codes">
        <UzFormField name="name">
          {({ field, error }) => (
            <UzInput
              {...field}
              value={(field.value as string | undefined) ?? ''}
              label="City name"
              placeholder="e.g. Pune"
              error={error}
              required
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="iataCode">
          {({ field, error }) => (
            <UzInput
              {...field}
              value={(field.value as string | undefined) ?? ''}
              onChange={(e) => field.onChange(e.target.value.toUpperCase())}
              label="City Code"
              placeholder="3 uppercase letters, e.g. PNQ"
              hint="Optional. Leave blank if none."
              maxLength={3}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="customCityCode">
          {({ field, error }) => (
            <UzInput
              {...field}
              value={(field.value as string | undefined) ?? ''}
              onChange={(e) => field.onChange(e.target.value.toUpperCase())}
              label="Custom City Code"
              placeholder="3 uppercase letters"
              hint="Optional. Leave blank if none."
              maxLength={3}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="neighbouringAirportCodes">
          {({ field, error }) => (
            <AirportCodesInput
              value={(field.value as string[] | undefined) ?? []}
              onChange={field.onChange}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      </UzCollapsibleSection>
      <UzCollapsibleSection
        title="Status & visibility"
        description="Whether this city is active, and where it appears"
      >
        {/* Active flag. Deactivating hides the city from the pickers/master
            filters (which default to active-only) without deleting it. Mirrors
            the list's bulk activate/deactivate, but per-city here. */}
        <UzFormField name="isActive">
          {({ field }) => (
            <UzSwitch
              label="Active"
              description="Inactive cities are hidden from pickers and the default master list, but not deleted."
              checked={Boolean(field.value)}
              onCheckedChange={field.onChange}
              disabled={pending}
            />
          )}
        </UzFormField>
        {/* ADR-0031: opt-in flag that publishes this city to the website
            (destination pickers, content authoring). The flag lives on the
            shared City master so the website reads it directly - no
            parallel curated list to maintain. */}
        <UzFormField name="isWebsite">
          {({ field }) => (
            <UzSwitch
              label="Show on website"
              description="Publish this city to the website (destinations, content). Off by default."
              checked={Boolean(field.value)}
              onCheckedChange={field.onChange}
              disabled={pending}
            />
          )}
        </UzFormField>
      </UzCollapsibleSection>
    </div>
  );
};

export const CityFormDrawer = ({
  open,
  onOpenChange,
  mode,
  city,
}: CityFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createCity = useCreateCity();
  const updateCity = useUpdateCity();
  const countriesQuery = useCountries();
  const pending = createCity.isPending || updateCity.isPending;

  const countryOptions = useMemo<CountryOptionItem[]>(
    () =>
      (countriesQuery.data ?? []).map((c) => ({
        value: c.id,
        label: `${c.name} (${c.isoCode.toUpperCase()})`,
        tboCode: c.isoCode.toUpperCase(),
      })),
    [countriesQuery.data],
  );

  const createForm = useForm<CreateCityRequest>({
    resolver: zodResolver(CreateCityRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateCityRequest>({
    resolver: zodResolver(UpdateCityRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(CREATE_DEFAULTS);
    } else if (city) {
      updateForm.reset({
        countryId: city.country.id,
        stateId: city.state?.id ?? undefined,
        name: city.name,
        iataCode: city.iataCode ?? '',
        customCityCode: city.customCityCode ?? '',
        neighbouringAirportCodes: city.neighbouringAirportCodes ?? [],
        tboCityId: city.tboCityId ?? undefined,
        additionalTboCityIds: city.additionalTboCityIds ?? [],
        isActive: city.isActive,
        isWebsite: city.isWebsite,
      });
    }
  }, [open, isCreate, city, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    await createCity.mutateAsync({ ...values, tboCityId: values.tboCityId || null });
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!city) return;
    await updateCity.mutateAsync({
      id: city.id,
      payload: { ...values, tboCityId: values.tboCityId || null },
    });
    onOpenChange(false);
  });

  const fields = (
    <CityFields
      pending={pending}
      countryOptions={countryOptions}
      countriesLoading={countriesQuery.isLoading}
    />
  );

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add new city' : `Edit ${city?.name ?? 'city'}`}
      description="Pick the country, then optionally the state (its list loads for that country). City Code and Custom City Code are optional 3-letter codes."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <>
          <UzForm form={updateForm} onSubmit={onUpdate}>
            {fields}
          </UzForm>
          {/* Outside the form on purpose: it saves on its own endpoints, so a
              station change does not wait on (or get lost with) "Save changes". */}
          {city && (
            <div className="mt-3">
              <CityTransportSection cityId={city.id} />
            </div>
          )}
        </>
      )}
    </UzDrawer>
  );
};
